import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, CreditCard, Crown, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/analytics";
import { PREMIUM_BENEFITS, TRIAL_DAYS, markPremiumOfferSeen } from "@/lib/premium";
import { TRIAL_CTA, TRIAL_DISCLOSURE, TRIAL_HEADLINE, TRIAL_SUBHEAD } from "@/lib/trial";
import { TrialConfirmDialog } from "@/components/TrialConfirmDialog";

interface Props {
  /** Called after the offer is dismissed or the trial has been activated. */
  onClose: () => void;
  /** Refresh entitlement-dependent UI once premium turns on. */
  onTrialStarted?: () => void | Promise<void>;
}

/**
 * Post-signup premium offer. The trial is the introductory period of the
 * monthly subscription, so it always requires a payment method — Stripe
 * Checkout on web, the platform purchase sheet inside the native apps.
 */
export function PremiumOfferModal({ onClose, onTrialStarted }: Props) {
  const [confirming, setConfirming] = useState(false);
  const { t } = useTranslation();

  const dismiss = async (reason: string) => {
    void track("upgrade_prompt_dismissed", { surface: "onboarding_offer", reason });
    await markPremiumOfferSeen();
    onClose();
  };

  return (
    <div className="modal-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="modal-panel w-full max-w-md rounded-3xl border border-rule bg-card shadow-2xl animate-in fade-in zoom-in-95">
        <div className="relative bg-gradient-to-br from-neon-pink via-purple-600 to-neon-blue px-6 pb-8 pt-6 text-center text-white">
          <button
            onClick={() => void dismiss("close_icon")}
            aria-label="Close premium offer"
            className="absolute right-4 top-4 text-white/80 transition hover:text-white"
          >
            <X className="size-5" />
          </button>
          <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-white/20 ring-2 ring-neon-yellow/70">
            <Crown className="size-7 text-neon-yellow" />
          </div>
          <h2 className="mt-3 font-[family-name:var(--font-display)] text-2xl font-extrabold">
            🎉 {t("monetization.welcome_to_inquira")}
          </h2>
          <p className="mt-1 text-sm font-semibold text-white/95">
            {t("monetization.free_forever_offer")}
          </p>
        </div>

        <div className="space-y-4 p-6">
          <ul className="grid gap-2">
            {PREMIUM_BENEFITS.map((benefit) => (
              <li key={benefit} className="flex items-center gap-2 text-sm text-ink">
                <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-neon-yellow/25">
                  <Check className="size-3.5 text-neon-pink" />
                </span>
                {benefit}
              </li>
            ))}
          </ul>

          <p className="flex items-start gap-2 rounded-xl bg-muted/70 p-3 text-xs leading-relaxed text-ink">
            <CreditCard className="mt-0.5 size-4 shrink-0 text-neon-pink" aria-hidden="true" />
            Start your free trial today. A valid payment method is required. After your {TRIAL_DAYS}-day trial
            ends, your subscription automatically renews at $9.99/month unless canceled before the trial ends.
          </p>

          <div className="space-y-2">
            <Button
              variant="scan"
              size="full"
              onClick={() => void dismiss("continue_free")}
              className="font-[family-name:var(--font-display)]"
            >
              {t("monetization.continue_with_free")}
            </Button>
            <div className="border-t border-rule pt-3">
              <p className="mb-2 text-center text-xs font-semibold text-ink-muted">{t("monetization.or_try_premium")} · {TRIAL_HEADLINE}</p>
              <Button variant="outline" size="full" onClick={() => setConfirming(true)}>
                <Sparkles className="size-4" /> {TRIAL_CTA}
              </Button>
              <p className="mt-1 text-center text-[11px] text-ink-muted">{TRIAL_SUBHEAD} · {TRIAL_DISCLOSURE}</p>
            </div>
          </div>
        </div>
      </div>
      {confirming && (
        <TrialConfirmDialog
          surface="onboarding_offer"
          onClose={() => setConfirming(false)}
          onActivated={async () => {
            await markPremiumOfferSeen();
            await onTrialStarted?.();
            onClose();
          }}
        />
      )}
    </div>
  );
}
